import React from 'react';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { LineBreakdownData } from '@utils/threatFormatter';

export interface LineByLineThreatBreakdownProps {
  lineData: LineBreakdownData[];
  onPositionClick?: (position: number, line: LineBreakdownData) => void;
}

export const LineByLineThreatBreakdown: React.FC<LineByLineThreatBreakdownProps> = ({
  lineData,
  onPositionClick,
}) => {
  // Filter to show only lines with threats
  const linesWithThreats = lineData.filter((line) => line.threats.length > 0);

  if (linesWithThreats.length === 0) {
    return null;
  }

  return (
    <div>
      <div style={{ borderBottom: '2px solid #f57c00', paddingBottom: '8px', marginBottom: '16px' }}>
        <Box variant="h2">Line-by-Line Breakdown ({linesWithThreats.length} lines with threats)</Box>
      </div>

      <SpaceBetween size="s">
        {linesWithThreats.map((line) => (
          <ExpandableSection
            key={`line-${line.lineNumber}`}
            headerText={`Line ${line.lineNumber}: ${line.content.length > 100 ? line.content.substring(0, 100) + '...' : line.content} (${line.threats.length} threat${line.threats.length !== 1 ? 's' : ''})`}
            variant="container"
          >
            <SpaceBetween size="m">
              {/* Line content with highlighted threats */}
              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  wordBreak: 'break-all',
                }}
              >
                <LineContentWithHighlights
                  content={line.content}
                  threats={line.threats}
                  onThreatClick={onPositionClick ? (pos) => onPositionClick(pos, line) : undefined}
                />
              </div>

              {/* Threats on this line */}
              <div>
                <Box variant="awsui-key-label">Threats on this line:</Box>
                <SpaceBetween size="xs">
                  {line.threats.map((threat, idx) => {
                    // Calculate position within the line using lineStartPos
                    const positionInLine = threat.position - (line.lineStartPos || 0);
                    
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#fafafa',
                          borderLeft: `4px solid ${getSeverityColor(threat.severity)}`,
                          borderRadius: '2px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                          <Box variant="strong">{threat.type}</Box>
                          <Badge color={mapSeverityToBadgeColor(threat.severity)}>{threat.severity}</Badge>
                        </div>
                        <Box variant="p" color="text-body-secondary" fontSize="body-s" margin={{ top: 'xs' }}>
                          <span
                            onClick={() => onPositionClick?.(threat.position, line)}
                            style={{
                              cursor: onPositionClick ? 'pointer' : 'default',
                              textDecoration: onPositionClick ? 'underline' : 'none',
                              color: onPositionClick ? '#0972d3' : '#424242',
                              fontWeight: onPositionClick ? '500' : 'normal',
                            }}
                            title={`Click to highlight this position`}
                          >
                            Position: {positionInLine} (in line)
                          </span>
                        </Box>
                      </div>
                    );
                  })}
                </SpaceBetween>
              </div>
            </SpaceBetween>
          </ExpandableSection>
        ))}
      </SpaceBetween>
    </div>
  );
};

interface LineContentWithHighlightsProps {
  content: string;
  threats: Array<{ position: number; severity: string }>;
  onThreatClick?: (position: number) => void;
}

const LineContentWithHighlights: React.FC<LineContentWithHighlightsProps> = ({
  content,
  threats,
  onThreatClick,
}) => {
  // Sort threats by position
  const sortedThreats = [...threats].sort((a, b) => a.position - b.position);

  if (sortedThreats.length === 0) {
    return <span>{content}</span>;
  }

  // Build segments: text before threat, threat character, text after threat, etc.
  const segments: Array<{
    type: 'text' | 'threat';
    content: string;
    position?: number;
    severity?: string;
  }> = [];

  let lastPos = 0;

  sortedThreats.forEach((threat) => {
    // Add text before threat
    if (threat.position > lastPos) {
      segments.push({
        type: 'text',
        content: content.substring(lastPos, threat.position),
      });
    }

    // Add threat character (or 1 char at threat position)
    segments.push({
      type: 'threat',
      content: content.substring(threat.position, threat.position + 1) || '■',
      position: threat.position,
      severity: threat.severity,
    });

    lastPos = threat.position + 1;
  });

  // Add remaining text
  if (lastPos < content.length) {
    segments.push({
      type: 'text',
      content: content.substring(lastPos),
    });
  }

  return (
    <>
      {segments.map((segment, idx) =>
        segment.type === 'text' ? (
          <span key={idx}>{segment.content}</span>
        ) : (
          <span
            key={idx}
            style={{
              backgroundColor: getSeverityColor(segment.severity || 'LOW'),
              color: 'white',
              padding: '2px 4px',
              borderRadius: '2px',
              fontWeight: 'bold',
              cursor: onThreatClick ? 'pointer' : 'default',
            }}
            onClick={() => onThreatClick?.(segment.position || 0)}
            title={`Position: ${segment.position}, Severity: ${segment.severity}`}
          >
            {segment.content}
          </span>
        )
      )}
    </>
  );
};

function getSeverityColor(severity: string): string {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return '#d32f2f'; // Red
    case 'HIGH':
      return '#f57c00'; // Orange
    case 'MEDIUM':
      return '#fbc02d'; // Yellow
    case 'LOW':
      return '#1976d2'; // Blue
    default:
      return '#757575'; // Gray
  }
}

function mapSeverityToBadgeColor(severity: string): 'blue' | 'green' | 'grey' | 'red' | 'severity-critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'severity-neutral' {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return 'severity-critical';
    case 'HIGH':
      return 'severity-high';
    case 'MEDIUM':
      return 'severity-medium';
    case 'LOW':
      return 'severity-low';
    default:
      return 'severity-neutral';
  }
}
